import { MouseEvent, useCallback } from 'react';

import { PlankSelectionItem } from '../state/types/PlankSelectionStore';

import usePlankSelectionScope from './usePlankSelectionScope';
import usePlankTableStore from './usePlankTableStore';

/** File-explorer-style Ctrl/Shift multi-select for Plank rows. Ctrl/Cmd-click toggles one row;
 * Shift-click selects every row between the anchor and the clicked row, bounded to the row's
 * column so ranges never cross columns; a plain click clears the selection without stopping
 * propagation, so the existing "open sidepanel" click still runs.
 *
 * @param item The row's PlankSelectionItem, or undefined when it is not selectable.
 * @param enabled Whether Plank multi-select is enabled.
 */
export default function usePlankItemSelection(item: PlankSelectionItem | undefined, enabled = true) {
  const scope = usePlankSelectionScope();
  const isSelectable = enabled && !!item;
  const isSelectedInStore = usePlankTableStore((state) => !!item && state.isPlankItemSelected(item.key));
  const hasSelection = usePlankTableStore((state) => state.plankSelection.size > 0);
  const plankSelectionAnchorKey = usePlankTableStore((state) => state.plankSelectionAnchorKey);
  const togglePlankItemSelection = usePlankTableStore((state) => state.togglePlankItemSelection);
  const selectPlankItemRange = usePlankTableStore((state) => state.selectPlankItemRange);
  const clearPlankSelection = usePlankTableStore((state) => state.clearPlankSelection);

  const selectRangeToHere = useCallback(() => {
    if (!item) return;
    const anchorIndex = scope && plankSelectionAnchorKey ? scope.indexOf(plankSelectionAnchorKey) : -1;
    const currentIndex = scope ? scope.indexOf(item.key) : -1;

    // No usable anchor in this column: fall back to toggling just this row.
    if (!scope || anchorIndex === -1 || currentIndex === -1) {
      togglePlankItemSelection(item);
      return;
    }

    const [start, end] = anchorIndex <= currentIndex ? [anchorIndex, currentIndex] : [currentIndex, anchorIndex];
    selectPlankItemRange(scope.items.slice(start, end + 1), scope.keys);
  }, [item, scope, plankSelectionAnchorKey, togglePlankItemSelection, selectPlankItemRange]);

  const handleSelectionClickCapture = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!isSelectable || !item) return;

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        event.stopPropagation();
        togglePlankItemSelection(item);
        return;
      }

      if (event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        selectRangeToHere();
        return;
      }

      if (hasSelection) clearPlankSelection();
    },
    [item, isSelectable, hasSelection, togglePlankItemSelection, selectRangeToHere, clearPlankSelection],
  );

  const isSelected = isSelectable && isSelectedInStore;

  return { isSelected, handleSelectionClickCapture };
}
